import { eligibleEnemies } from './eligibility.js';
import { SeededRandom } from './random.js';
function intersects(left, right) {
    return left.some((value) => right.includes(value));
}
export function selectMutators(catalog, profile, theme, history, random) {
    if (profile.mutatorCount === 0)
        return [];
    const allowed = catalog.mutators.filter((mutator) => theme.mutatorIds.includes(mutator.id) &&
        mutator.allowedDifficultyIds.includes(profile.id) &&
        intersects(mutator.compatibleThemeTags, theme.enemyTags));
    const recent = new Set(history?.recentMutatorIds ?? []);
    const selected = [];
    while (selected.length < profile.mutatorCount && selected.length < allowed.length) {
        const remaining = allowed.filter((mutator) => !selected.some((entry) => entry.id === mutator.id));
        selected.push(random.weightedPick(remaining, (mutator) => mutator.selectionWeight * (recent.has(mutator.id) ? 0.2 : 1)));
    }
    return selected;
}
function historyPenalty(enemy, history) {
    return history?.recentEnemyIds?.includes(enemy.id) ? 0.25 : 1;
}
function preferredTagMultiplier(enemy, mutators) {
    const preferred = mutators.flatMap((mutator) => mutator.effects.preferEnemyTags ?? []);
    if (preferred.length === 0)
        return 1;
    return intersects(enemy.tags, preferred) || intersects(enemy.roles, preferred) ? 1.75 : 0.8;
}
export function selectBosses(catalog, theme, targetTier, bossCount, history, random) {
    const eligible = eligibleEnemies(catalog.enemies, theme, targetTier, true);
    const finalBoss = random.weightedPick(eligible, (enemy) => enemy.selectionWeight * enemy.threat * historyPenalty(enemy, history));
    if (bossCount === 1)
        return [finalBoss];
    const midCandidates = eligible.filter((enemy) => enemy.id !== finalBoss.id);
    const midBoss = random.weightedPick(midCandidates, (enemy) => enemy.selectionWeight * (1 / Math.max(1, enemy.threat)) * historyPenalty(enemy, history));
    return [midBoss, finalBoss];
}
function aggregateMembers(selected) {
    const counts = new Map();
    for (const enemy of selected) {
        const current = counts.get(enemy.id);
        if (current)
            current.count += 1;
        else
            counts.set(enemy.id, { enemy, count: 1 });
    }
    return [...counts.values()]
        .sort((left, right) => left.enemy.id.localeCompare(right.enemy.id))
        .map(({ enemy, count }) => ({ enemyId: enemy.id, count, roles: [...enemy.roles] }));
}
export function generateOrdinaryEncounter(input) {
    const candidates = eligibleEnemies(input.catalog.enemies, input.theme, input.targetTier, false);
    const depthProgress = Math.min(1, input.room.depth / Math.max(1, input.maximumDepth));
    const mutatorBudgetMultiplier = input.mutators.reduce((product, mutator) => product * (mutator.effects.encounterBudgetMultiplier ?? 1), 1);
    const budget = input.profile.baseThreatPerPlayer *
        input.partySize *
        (1 + input.profile.depthThreatBonus * depthProgress) *
        mutatorBudgetMultiplier;
    const sizeBonus = input.mutators.reduce((sum, mutator) => sum + (mutator.effects.maxEncounterSizeBonus ?? 0), 0);
    const maxSize = input.profile.maxEncounterSize + sizeBonus;
    const selected = [];
    let plannedThreat = 0;
    const minimumThreat = Math.min(...candidates.map((enemy) => enemy.threat));
    while (selected.length < maxSize && (plannedThreat < budget * 0.9 || selected.length === 0)) {
        const remainingBudget = Math.max(0, budget - plannedThreat);
        const fitting = candidates.filter((enemy) => enemy.threat <= remainingBudget + minimumThreat * 0.75);
        const pool = fitting.length > 0
            ? fitting
            : [...candidates].sort((a, b) => a.threat - b.threat).slice(0, 2);
        const selectedRoles = new Set(selected.flatMap((enemy) => enemy.roles));
        const enemy = input.random.weightedPick(pool, (candidate) => {
            const runCount = input.runEnemyCounts.get(candidate.id) ?? 0;
            const introducesRole = candidate.roles.some((role) => !selectedRoles.has(role));
            return ((candidate.selectionWeight *
                historyPenalty(candidate, input.history) *
                preferredTagMultiplier(candidate, input.mutators) *
                (introducesRole ? 1.5 : 1) *
                1) /
                (1 + runCount * 0.55));
        });
        selected.push(enemy);
        plannedThreat += enemy.threat;
        input.runEnemyCounts.set(enemy.id, (input.runEnemyCounts.get(enemy.id) ?? 0) + 1);
    }
    return {
        id: `encounter-${String(input.encounterIndex).padStart(3, '0')}`,
        kind: 'ordinary',
        threatBudget: Number(budget.toFixed(3)),
        plannedThreat: Number(plannedThreat.toFixed(3)),
        difficultyScale: 1,
        members: aggregateMembers(selected),
        mutatorIds: input.mutators.map((mutator) => mutator.id),
    };
}
export function generateBossEncounter(encounterIndex, boss, profile, mutators) {
    return {
        id: `encounter-${String(encounterIndex).padStart(3, '0')}`,
        kind: 'boss',
        threatBudget: Number((boss.threat * profile.bossDifficultyScale).toFixed(3)),
        plannedThreat: boss.threat,
        difficultyScale: profile.bossDifficultyScale,
        members: [{ enemyId: boss.id, count: 1, roles: [...boss.roles] }],
        mutatorIds: mutators.map((mutator) => mutator.id),
    };
}
//# sourceMappingURL=encounters.js.map